import { Box, Tooltip, Typography } from "@mui/material";

import { ds } from "../theme/ds";
import { chartPalette } from "../theme/tokens";
import type { WidgetProps } from "./types";

// Rollout timeline (2026-09-28, Tvilum pitch, Bill). One row per workstream;
// each bar sits on a month axis by calendar date and takes its colour from
// the owner it belongs to (`props.series`, chartPalette in order: Blue,
// Green, Orange). A bar whose start equals its end is a milestone diamond.
// Plain CSS grid, no chart library; the only interaction is a tooltip with
// the bar's full label and dates. Design system: 16px navy title, 11px gray
// caption, dashed gray-200 gridlines on the month starts, no legend chrome
// beyond the swatches under the rows.

const DAY = 86_400_000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const LABEL_COL = { xs: 168, md: 248 };
const ROW_H = 36;
const BAR_H = 22;

function utc(iso: string) {
    return Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
}

function prettyDate(iso: string) {
    const t = new Date(utc(iso));
    return `${t.getUTCDate()} ${MONTHS[t.getUTCMonth()]}`;
}

export function Gantt({ widget }: WidgetProps<"gantt">) {
    const { title, caption, source, start, end, series, rows, axis } = widget.props;
    const t0 = utc(start);
    const t1 = utc(end) + DAY; // the axis end is inclusive
    const span = t1 - t0;
    const pos = (t: number) => (100 * (Math.min(Math.max(t, t0), t1) - t0)) / span;

    // Month bands across the axis: the header labels and the gridlines.
    // A day axis (opt-in, a 10-day onboarding plan) labels each day "Day N" instead.
    const months: { from: number; to: number; label: string }[] = [];
    if (axis === "day")
        for (let t = t0, n = 1; t < t1; t += DAY, n++) months.push({ from: t, to: t + DAY, label: `Day ${n}` });
    else for (let d = new Date(t0); d.getTime() < t1; ) {
        const next = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
        months.push({
            from: d.getTime(),
            to: Math.min(next, t1),
            label: `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`,
        });
        d = new Date(next);
    }

    const gridlines = months.slice(1).map((m) => (
        <Box
            key={m.from}
            sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${pos(m.from)}%`,
                borderLeft: `1px dashed ${ds.gray200}`,
            }}
        />
    ));

    return (
        <Box>
            {title && (
                <Typography
                    component="h3"
                    sx={{ fontSize: 16, fontWeight: 700, color: ds.navy, mb: 0.5 }}
                >
                    {title}
                </Typography>
            )}
            {caption && (
                <Typography
                    component="div"
                    sx={{ fontSize: ds.text.xs, color: ds.gray500, mb: 2, maxWidth: 760 }}
                >
                    {caption}
                </Typography>
            )}
            <Box sx={{ overflowX: "auto" }}>
                <Box
                    sx={{
                        minWidth: 680,
                        display: "grid",
                        gridTemplateColumns: {
                            xs: `${LABEL_COL.xs}px 1fr`,
                            md: `${LABEL_COL.md}px 1fr`,
                        },
                        border: `1px solid ${ds.gray200}`,
                        borderRadius: `${ds.radiusLg}px`,
                        overflow: "hidden",
                        bgcolor: ds.white,
                    }}
                >
                    {/* Header row: an empty label cell, then the month bands. */}
                    <Box sx={{ borderBottom: `1px solid ${ds.gray200}`, bgcolor: ds.gray50 }} />
                    <Box
                        sx={{
                            position: "relative",
                            height: 32,
                            borderBottom: `1px solid ${ds.gray200}`,
                            bgcolor: ds.gray50,
                        }}
                    >
                        {months.map((m) => {
                            const width = pos(m.to) - pos(m.from);
                            return (
                                <Typography
                                    key={m.from}
                                    component="div"
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        bottom: 0,
                                        left: `${pos(m.from)}%`,
                                        width: `${width}%`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: ds.text.xs,
                                        fontWeight: 600,
                                        color: ds.gray600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                    }}
                                >
                                    {width >= 8 ? m.label : ""}
                                </Typography>
                            );
                        })}
                        {gridlines}
                    </Box>

                    {rows.map((row, i) => {
                        const last = i === rows.length - 1;
                        const rule = last ? "none" : `1px solid ${ds.gray100}`;
                        return [
                            <Typography
                                key={`${row.label}-label`}
                                component="div"
                                sx={{
                                    px: 2,
                                    height: ROW_H,
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: ds.text.sm,
                                    fontWeight: 600,
                                    color: ds.navy,
                                    lineHeight: 1.25,
                                    borderBottom: rule,
                                    borderRight: `1px solid ${ds.gray200}`,
                                }}
                            >
                                {row.label}
                            </Typography>,
                            <Box
                                key={`${row.label}-track`}
                                sx={{ position: "relative", height: ROW_H, borderBottom: rule }}
                            >
                                {gridlines}
                                {row.bars.map((bar) => {
                                    const color = chartPalette[bar.series % chartPalette.length];
                                    const left = pos(utc(bar.start));
                                    const milestone = bar.start === bar.end;
                                    const when = milestone
                                        ? prettyDate(bar.start)
                                        : `${prettyDate(bar.start)} to ${prettyDate(bar.end)}`;
                                    const tip = `${bar.label}: ${when}`;
                                    if (milestone) {
                                        return (
                                            <Tooltip key={tip} title={tip} arrow>
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: (ROW_H - 14) / 2,
                                                        left: `calc(${left}% - 7px)`,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 14,
                                                            height: 14,
                                                            bgcolor: color,
                                                            transform: "rotate(45deg)",
                                                            borderRadius: "2px",
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontSize: ds.text.xs,
                                                            fontWeight: 600,
                                                            color: ds.navy,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {bar.label}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        );
                                    }
                                    const width = pos(utc(bar.end) + DAY) - left;
                                    return (
                                        <Tooltip key={tip} title={tip} arrow>
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: (ROW_H - BAR_H) / 2,
                                                    left: `${left}%`,
                                                    width: `${width}%`,
                                                    height: BAR_H,
                                                    lineHeight: `${BAR_H}px`,
                                                    bgcolor: color,
                                                    borderRadius: "4px",
                                                    px: 1,
                                                    color: ds.white,
                                                    fontSize: ds.text.xs,
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    boxSizing: "border-box",
                                                }}
                                            >
                                                {bar.label}
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>,
                        ];
                    })}
                </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
                {series.map((name, i) => (
                    <Box key={name} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "3px",
                                bgcolor: chartPalette[i % chartPalette.length],
                            }}
                        />
                        <Typography component="span" sx={{ fontSize: ds.text.sm, color: ds.gray600 }}>
                            {name}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Typography component="div" sx={{ fontSize: ds.text.xs, color: ds.gray500, mt: 1 }}>
                Source: {source}
            </Typography>
        </Box>
    );
}
